import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabaseClient';
import { useAuth } from '@/contexts/AuthContext';
import { getHomeRoute } from '@/lib/api';
import { enterFullscreenWithEscapeLock } from '@/lib/fullscreenUtils';
import { QRCodeSVG } from 'qrcode.react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/card';
import { toast } from 'sonner';
import { ShieldCheck, Copy, Loader2 } from 'lucide-react';

const _decodeJwtPayload = (token) => {
  try {
    return JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return {};
  }
};

const MFAEnroll = () => {
  const navigate  = useNavigate();
  const { login } = useAuth();

  const [totpUri,   setTotpUri]   = useState('');
  const [secret,    setSecret]    = useState('');
  const [factorId,  setFactorId]  = useState('');
  const [code,      setCode]      = useState('');
  const [loading,   setLoading]   = useState(false);
  const [enrolling, setEnrolling] = useState(true);
  const [copied,    setCopied]    = useState(false);

  const startEnrollment = useCallback(async () => {
    setEnrolling(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session) { navigate('/login'); return; }

      // Remove any existing unverified factors first (clean slate)
      const { data: factors } = await supabase.auth.mfa.listFactors();
      const unverified = (factors?.totp || []).filter(f => f.status !== 'verified');
      await Promise.all(unverified.map(f => supabase.auth.mfa.unenroll({ factorId: f.id })));

      const { data, error } = await supabase.auth.mfa.enroll({ factorType: 'totp', issuer: 'KiranaFlow' });
      if (error) throw error;
      setTotpUri(data.totp.uri);
      setSecret(data.totp.secret);
      setFactorId(data.id);
    } catch (e) {
      toast.error('Could not start 2FA setup: ' + (e.message || 'Unknown error'));
    } finally {
      setEnrolling(false);
    }
  }, [navigate]);

  useEffect(() => { startEnrollment(); }, [startEnrollment]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (code.length !== 6) { toast.error('Enter the 6-digit code from your app'); return; }
    setLoading(true);
    try {
      const { data: challenge, error: challengeErr } = await supabase.auth.mfa.challenge({ factorId });
      if (challengeErr) throw challengeErr;

      const { data: verified, error: verifyErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: challenge.id,
        code: code.trim(),
      });
      if (verifyErr) throw verifyErr;

      // auth-js v2.x returns the session fields directly on data (not nested under .session)
      const aal2Token    = verified.access_token;
      const refreshToken = verified.refresh_token;
      localStorage.setItem('kiranaflow_token', aal2Token);
      localStorage.setItem('kiranaflow_refresh_token', refreshToken);

      const pendingUser = JSON.parse(sessionStorage.getItem('kiranaflow_pending_user') || 'null');
      sessionStorage.removeItem('kiranaflow_pending_user');
      sessionStorage.removeItem('kiranaflow_token'); // aal1 gate token — aal2 is now in localStorage

      const payload = _decodeJwtPayload(aal2Token);
      const userData = pendingUser || {
        id: payload.sub, role: payload.role, owner_id: payload.owner_id,
        store_id: payload.store_id, owner_name: '', shop_name: '',
        mobile: '', membership_tier: payload.membership_tier || 'starter',
      };

      await login({ token: aal2Token, refresh_token: refreshToken, user: userData });
      toast.success('Two-factor authentication enabled!');

      navigate(getHomeRoute(userData.role), { replace: true });
      enterFullscreenWithEscapeLock();
    } catch (e) {
      toast.error(e.message?.includes('Invalid') ? 'Incorrect code — check your app and try again' : (e.message || 'Verification failed'));
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(secret).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto bg-primary text-primary-foreground w-14 h-14 rounded-full flex items-center justify-center">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Set Up Two-Factor Auth</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Scan the QR code with Google Authenticator or Authy, then enter the 6-digit code to activate.
            </p>
          </div>
        </CardHeader>

        <CardContent className="space-y-5">
          {enrolling ? (
            <div className="flex items-center justify-center py-12 gap-3 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" /> Preparing setup…
            </div>
          ) : (
            <>
              {/* QR Code */}
              {totpUri && (
                <div className="flex flex-col items-center gap-3">
                  <div className="p-3 bg-white rounded-xl border shadow-sm">
                    <QRCodeSVG value={totpUri} size={200} />
                  </div>
                  <p className="text-xs text-muted-foreground text-center">
                    Open your authenticator app → tap <strong>+</strong> → <strong>Scan QR code</strong>
                  </p>
                </div>
              )}

              {/* Manual secret fallback */}
              {secret && (
                <div className="rounded-lg bg-muted/50 border px-3 py-2.5 space-y-1">
                  <p className="text-xs text-muted-foreground font-medium">Can't scan? Enter this key manually:</p>
                  <div className="flex items-center gap-2">
                    <code className="text-xs font-mono flex-1 break-all text-foreground">{secret}</code>
                    <button onClick={copySecret} className="p-1 rounded hover:bg-black/10 flex-shrink-0" title="Copy">
                      <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                    </button>
                  </div>
                  {copied && <p className="text-xs text-emerald-600">Copied!</p>}
                </div>
              )}

              {/* Code input */}
              <form onSubmit={handleActivate} className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">6-digit code from your app</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={6}
                    placeholder="123456"
                    value={code}
                    onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
                    className="h-12 text-center text-xl font-mono tracking-widest"
                    autoFocus
                    autoComplete="one-time-code"
                  />
                </div>
                <Button type="submit" className="w-full h-12 text-base font-bold gap-2" disabled={loading || code.length !== 6}>
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Activating…</> : 'Activate 2FA'}
                </Button>
              </form>
            </>
          )}
        </CardContent>

        <CardFooter className="justify-center">
          <p className="text-xs text-muted-foreground text-center">
            You'll need this app every time you log in. Keep it installed on your phone.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
};

export default MFAEnroll;
